import { FunctionEntry, PhysicsObject, GraphSettings } from '@/types/simulation';
import { computeGraphPoints } from '@/utils/mathParser';
import { worldToCanvas } from '@/utils/physics';

export interface TitleOptions {
  text: string;
  showDate: boolean;
  position: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  fontSize: number;      // 12 ~ 48
  color: string;         // hex color
  fontWeight: 'normal' | 'bold' | 'light';
}

export interface ExportRenderOptions {
  functions: FunctionEntry[];
  objects: PhysicsObject[];
  settings: GraphSettings;
  width: number;
  height: number;
  // visibility toggles
  showGrid: boolean;
  showAxisLabels: boolean;
  showTrails: boolean;
  showObjects: boolean;
  showObjectOrientation: boolean;
  showVelocityArrow: boolean;
  visibleFunctionIds: Set<string>;
  visibleObjectIds: Set<string>;
  // style
  bgColor: string;
  theme: 'dark' | 'light' | 'picture';
  scale: number;
  // title
  title?: TitleOptions | null;
}

function calcAdaptiveStep(rangeMin: number, rangeMax: number, canvasPixels: number, minGapPx = 60): number {
  const range = rangeMax - rangeMin;
  const rawStep = (range / canvasPixels) * minGapPx;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let nice: number;
  if (norm < 1.5) nice = 1;
  else if (norm < 3.5) nice = 2;
  else if (norm < 7.5) nice = 5;
  else nice = 10;
  return nice * mag;
}

function fmtTick(v: number, step: number): string {
  if (Math.abs(v) < step * 0.001) return '0';
  const decimals = Math.max(0, -Math.floor(Math.log10(step)));
  return decimals > 0 ? v.toFixed(decimals) : String(Math.round(v));
}

function shadeHex(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
  return `rgb(${r},${g},${b})`;
}

function drawArrowExport(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number, x2: number, y2: number,
  color: string, lineWidth: number, label: string
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 5) return;
  const angle = Math.atan2(dy, dx);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  const headLen = 10;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
  ctx.closePath(); ctx.fill();
  ctx.font = '12px Inter, sans-serif';
  ctx.fillText(label, x2 + 8, y2 - 5);
  ctx.restore();
}

/** タイトルと日付を描画 */
function drawTitle(
  ctx: CanvasRenderingContext2D,
  title: TitleOptions,
  width: number,
  height: number,
  theme: 'dark' | 'light' | 'picture'
) {
  const { text, showDate, position, fontSize, color, fontWeight } = title;
  if (!text.trim() && !showDate) return;

  const dateStr = showDate
    ? new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  ctx.save();
  const weight = fontWeight === 'bold' ? '700' : fontWeight === 'light' ? '300' : '400';
  ctx.font = `${weight} ${fontSize}px Inter, sans-serif`;
  ctx.fillStyle = color;

  const lines: string[] = [];
  if (text.trim()) lines.push(text.trim());
  if (dateStr) lines.push(dateStr);

  const lineHeight = fontSize * 1.4;
  const totalHeight = lines.length * lineHeight;
  const maxWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));

  const padding = 16;
  let x: number;
  let y: number;

  switch (position) {
    case 'top-left':
      x = padding;
      y = padding + fontSize;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      break;
    case 'top-center':
      x = width / 2;
      y = padding + fontSize;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      break;
    case 'top-right':
      x = width - padding;
      y = padding + fontSize;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';
      break;
    case 'bottom-left':
      x = padding;
      y = height - padding - totalHeight + fontSize;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      break;
    case 'bottom-center':
      x = width / 2;
      y = height - padding - totalHeight + fontSize;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      break;
    case 'bottom-right':
      x = width - padding;
      y = height - padding - totalHeight + fontSize;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';
      break;
  }

  // Draw subtle background pill for readability
  const bgPadX = 12;
  const bgPadY = 6;
  const isDarkTheme = theme === 'dark';
  const bgAlpha = isDarkTheme ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.55)';
  const bgRadius = 8;

  if (lines.length > 0) {
    ctx.save();
    ctx.fillStyle = bgAlpha;
    const bgW = maxWidth + bgPadX * 2;
    const bgH = totalHeight + bgPadY * 2;
    let bgX = x;
    let bgY = y - fontSize - bgPadY;
    if (position.includes('center')) bgX = x - bgW / 2;
    if (position.includes('right')) bgX = x - bgW;
    roundRect(ctx, bgX, bgY, bgW, bgH, bgRadius);
    ctx.fill();
    ctx.restore();
  }

  lines.forEach((line, i) => {
    ctx.fillText(line, x, y + i * lineHeight);
  });

  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export function renderToCanvas(opts: ExportRenderOptions): HTMLCanvasElement {
  const {
    functions, objects, settings, width, height,
    showGrid, showAxisLabels, showTrails, showObjects,
    showObjectOrientation, showVelocityArrow,
    visibleFunctionIds, visibleObjectIds,
    bgColor, theme, scale, title,
  } = opts;

  const W = Math.round(width * scale);
  const H = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  ctx.scale(scale, scale);

  const { xMin, xMax, yMin, yMax } = settings;
  const coordSys = { settings, width, height };

  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const pxPerUnitX = width / xRange;
  const pxPerUnitY = height / yRange;
  const pxPerUnit = Math.min(pxPerUnitX, pxPerUnitY);
  const refPxPerUnit = width / 20;
  const zoomRatio = pxPerUnit / refPxPerUnit;
  const zoomLog = Math.log2(Math.max(zoomRatio, 0.01));

  const adaptiveStepX = calcAdaptiveStep(xMin, xMax, width, 70);
  const adaptiveStepY = calcAdaptiveStep(yMin, yMax, height, 55);
  const gridStep = Math.max(adaptiveStepX, adaptiveStepY);
  const minorStep = gridStep / 5;

  const fnLineWidth = Math.max(1.0, Math.min(4.0, 2.2 + zoomLog * 0.35));
  const axisWidth   = Math.max(1.0, Math.min(3.0, 1.8 + zoomLog * 0.25));
  const majorWidth  = Math.max(0.4, Math.min(1.5, 0.8 + zoomLog * 0.12));
  const minorWidth  = Math.max(0.2, Math.min(0.8, 0.4 + zoomLog * 0.06));
  const fontSize    = Math.max(9, Math.min(14, 11 + zoomLog * 1.2));

  type Pal = { bgStart: string; bgEnd: string; gridMinor: string; gridMajor: string; axis: string; axisLabel: string };
  const palettes: Record<string, Pal> = {
    dark:    { bgStart: '#0f0f1e', bgEnd: '#1a1a2e', gridMinor: 'rgba(255,255,255,0.05)', gridMajor: 'rgba(255,255,255,0.12)', axis: 'rgba(255,255,255,0.50)', axisLabel: 'rgba(255,255,255,0.50)' },
    light:   { bgStart: '#f5f7ff', bgEnd: '#edf0fb', gridMinor: 'rgba(0,0,0,0.04)',       gridMajor: 'rgba(0,0,0,0.10)',       axis: 'rgba(0,0,0,0.50)',       axisLabel: 'rgba(0,0,0,0.55)' },
    picture: { bgStart: '#ffffff', bgEnd: '#f8f8ff', gridMinor: 'rgba(0,0,0,0.05)',       gridMajor: 'rgba(0,0,0,0.14)',       axis: 'rgba(0,0,0,0.70)',       axisLabel: 'rgba(0,0,0,0.70)' },
  };
  const pal = palettes[theme] ?? palettes.picture;

  // Background
  if (bgColor === 'transparent') {
    ctx.clearRect(0, 0, width, height);
  } else if (bgColor === 'theme') {
    const bg = ctx.createLinearGradient(0, 0, 0, height);
    bg.addColorStop(0, pal.bgStart);
    bg.addColorStop(1, pal.bgEnd);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);
  } else {
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);
  }

  // Grid
  if (showGrid) {
    const minorPxGap = minorStep * pxPerUnitX;
    if (minorPxGap >= 10) {
      ctx.strokeStyle = pal.gridMinor;
      ctx.lineWidth = minorWidth;
      for (let gx = Math.ceil(xMin / minorStep) * minorStep; gx <= xMax + 1e-9; gx += minorStep) {
        const { cx } = worldToCanvas(gx, 0, coordSys);
        ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, height); ctx.stroke();
      }
      for (let gy = Math.ceil(yMin / minorStep) * minorStep; gy <= yMax + 1e-9; gy += minorStep) {
        const { cy } = worldToCanvas(0, gy, coordSys);
        ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(width, cy); ctx.stroke();
      }
    }
    ctx.strokeStyle = pal.gridMajor;
    ctx.lineWidth = majorWidth;
    for (let gx = Math.ceil(xMin / gridStep) * gridStep; gx <= xMax + 1e-9; gx += gridStep) {
      const { cx } = worldToCanvas(gx, 0, coordSys);
      ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, height); ctx.stroke();
    }
    for (let gy = Math.ceil(yMin / gridStep) * gridStep; gy <= yMax + 1e-9; gy += gridStep) {
      const { cy } = worldToCanvas(0, gy, coordSys);
      ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(width, cy); ctx.stroke();
    }
  }

  // Axes
  const origin = worldToCanvas(0, 0, coordSys);
  ctx.strokeStyle = pal.axis;
  ctx.lineWidth = axisWidth;
  ctx.beginPath(); ctx.moveTo(0, origin.cy); ctx.lineTo(width, origin.cy); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(origin.cx, 0); ctx.lineTo(origin.cx, height); ctx.stroke();

  // Axis labels
  if (showAxisLabels) {
    ctx.fillStyle = pal.axisLabel;
    ctx.font = `${fontSize}px Inter, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (let gx = Math.ceil(xMin / gridStep) * gridStep; gx <= xMax + 1e-9; gx += gridStep) {
      if (Math.abs(gx) < gridStep * 0.001) continue;
      const { cx } = worldToCanvas(gx, 0, coordSys);
      const labelY = Math.min(Math.max(origin.cy + 4, 4), height - fontSize - 2);
      ctx.fillText(fmtTick(gx, gridStep), cx, labelY);
    }
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let gy = Math.ceil(yMin / gridStep) * gridStep; gy <= yMax + 1e-9; gy += gridStep) {
      if (Math.abs(gy) < gridStep * 0.001) continue;
      const { cy } = worldToCanvas(0, gy, coordSys);
      const labelX = Math.min(Math.max(origin.cx - 6, 6), width - 6);
      ctx.fillText(fmtTick(gy, gridStep), labelX, cy);
    }
  }

  // Functions
  functions
    .filter((f) => f.visible && !f.error && f.expression.trim() && visibleFunctionIds.has(f.id))
    .forEach((fn) => {
      const sampleCount = Math.round(Math.min(2000, Math.max(600, width * (1 + zoomRatio * 0.5))));
      const pts = computeGraphPoints(fn.expression, xMin, xMax, sampleCount);
      ctx.save();
      ctx.strokeStyle = fn.color;
      ctx.lineWidth = fnLineWidth;
      ctx.lineJoin = 'round';
      if (theme === 'dark') {
        ctx.shadowColor = fn.color;
        ctx.shadowBlur = Math.max(3, 6 + zoomLog);
      }
      ctx.beginPath();
      let penDown = false;
      pts.forEach(({ x: wx, y: wy }) => {
        if (isNaN(wy) || !isFinite(wy) || wy < yMin - 100 || wy > yMax + 100) {
          penDown = false; return;
        }
        const { cx, cy } = worldToCanvas(wx, wy, coordSys);
        if (!penDown) { ctx.moveTo(cx, cy); penDown = true; }
        else ctx.lineTo(cx, cy);
      });
      ctx.stroke();
      ctx.restore();
    });

  // Trails
  if (showTrails) {
    objects
      .filter((o) => visibleObjectIds.has(o.id))
      .forEach((obj) => {
        if (obj.trail.length < 2) return;
        ctx.save();
        ctx.lineWidth = 1.5;
        for (let i = 1; i < obj.trail.length; i++) {
          const alpha = i / obj.trail.length;
          ctx.strokeStyle = obj.color + Math.round(alpha * 180).toString(16).padStart(2, '0');
          ctx.beginPath();
          const p0 = worldToCanvas(obj.trail[i - 1].x, obj.trail[i - 1].y, coordSys);
          const p1 = worldToCanvas(obj.trail[i].x, obj.trail[i].y, coordSys);
          ctx.moveTo(p0.cx, p0.cy);
          ctx.lineTo(p1.cx, p1.cy);
          ctx.stroke();
        }
        ctx.restore();
      });
  }

  // Objects
  if (showObjects) {
    objects
      .filter((o) => visibleObjectIds.has(o.id))
      .forEach((obj) => {
        const { cx, cy } = worldToCanvas(obj.x, obj.y, coordSys);
        // ワールド単位の半径をキャンバスピクセルに変換（useGraphCanvas.ts と同じ計算）
        const radiusWorld = obj.size / 2;
        const r = radiusWorld * pxPerUnitY;
        if (obj.x < xMin - radiusWorld || obj.x > xMax + radiusWorld ||
            obj.y < yMin - radiusWorld || obj.y > yMax + radiusWorld) return;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(obj.angle);

        const strokeAlpha = theme === 'picture' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.3)';
        ctx.strokeStyle = strokeAlpha;
        ctx.lineWidth = 1.5;

        if (obj.shape === 'circle') {
          const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, r * 0.05, 0, 0, r);
          grad.addColorStop(0, 'rgba(255,255,255,0.5)');
          grad.addColorStop(0.35, obj.color);
          grad.addColorStop(1, shadeHex(obj.color, -40));
          ctx.fillStyle = grad;
          ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();

          if (showObjectOrientation) {
            ctx.save();
            ctx.strokeStyle = 'rgba(255,255,255,0.55)';
            ctx.lineWidth = 1.5;
            ctx.setLineDash([r * 0.4, r * 0.6]);
            ctx.beginPath(); ctx.arc(0, 0, r * 0.65, 0, Math.PI * 2); ctx.stroke();
            ctx.setLineDash([]);
            ctx.lineWidth = 2;
            ctx.strokeStyle = 'rgba(255,255,255,0.7)';
            ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(r, 0); ctx.stroke();
            ctx.restore();
          }
          ctx.fillStyle = 'rgba(255,255,255,0.22)';
          ctx.beginPath(); ctx.arc(-r * 0.27, -r * 0.27, r * 0.32, 0, Math.PI * 2); ctx.fill();
        } else if (obj.shape === 'square') {
          ctx.fillStyle = obj.color;
          ctx.beginPath(); ctx.rect(-r, -r, r * 2, r * 2); ctx.fill(); ctx.stroke();
          if (showObjectOrientation) {
            ctx.save();
            ctx.strokeStyle = 'rgba(255,255,255,0.25)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(-r, 0); ctx.lineTo(r, 0);
            ctx.moveTo(0, -r); ctx.lineTo(0, r);
            ctx.moveTo(-r, -r); ctx.lineTo(r, r);
            ctx.stroke();
            ctx.restore();
          }
          ctx.fillStyle = 'rgba(255,255,255,0.6)';
          ctx.beginPath(); ctx.arc(r * 0.6, -r * 0.6, r * 0.15, 0, Math.PI * 2); ctx.fill();
        } else if (obj.shape === 'triangle') {
          ctx.fillStyle = obj.color;
          ctx.beginPath();
          ctx.moveTo(0, -r); ctx.lineTo(r * 0.866, r * 0.5); ctx.lineTo(-r * 0.866, r * 0.5);
          ctx.closePath(); ctx.fill(); ctx.stroke();
          if (showObjectOrientation) {
            ctx.save();
            ctx.strokeStyle = 'rgba(255,255,255,0.3)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, -r * 0.5); ctx.lineTo(r * 0.433, r * 0.25); ctx.lineTo(-r * 0.433, r * 0.25);
            ctx.closePath(); ctx.stroke();
            ctx.restore();
          }
          ctx.fillStyle = 'rgba(255,255,255,0.5)';
          ctx.beginPath(); ctx.arc(0, -r * 0.7, r * 0.12, 0, Math.PI * 2); ctx.fill();
        }

        ctx.restore();

        if (showVelocityArrow) {
          const vScale = 20;
          const vex = cx + obj.vx * vScale;
          const vey = cy - obj.vy * vScale;
          drawArrowExport(ctx, cx, cy, vex, vey, theme === 'picture' ? '#0070a0' : '#00d9ff', 2, 'v');
        }
      });
  }

  // Title overlay (drawn last so it's on top)
  if (title) {
    drawTitle(ctx, title, width, height, theme);
  }

  return canvas;
}

export function renderToPNG(opts: ExportRenderOptions, quality = 1.0): Promise<Blob> {
  const canvas = renderToCanvas(opts);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('PNG生成に失敗しました')),
      'image/png',
      quality
    );
  });
}

export function renderToJPG(opts: ExportRenderOptions, quality = 0.92): Promise<Blob> {
  const jpgOpts = { ...opts, bgColor: opts.bgColor === 'transparent' ? '#ffffff' : opts.bgColor };
  const canvas = renderToCanvas(jpgOpts);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('JPG生成に失敗しました')),
      'image/jpeg',
      quality
    );
  });
}

export function renderToWebP(opts: ExportRenderOptions, quality = 0.92): Promise<Blob> {
  const canvas = renderToCanvas(opts);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('WebP生成に失敗しました')),
      'image/webp',
      quality
    );
  });
}

export interface ExcelExportData {
  functions: { expression: string; color: string; visible: boolean }[];
  objects: {
    id: string;
    shape: string;
    x: number;
    y: number;
    vx: number;
    vy: number;
    mass: number;
    size: number;
    color: string;
  }[];
  settings: {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
  };
  trailData?: { objectId: string; points: { x: number; y: number }[] }[];
}

export async function renderToExcel(data: ExcelExportData): Promise<Blob> {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');

  const wb = XLSX.utils.book_new();

  const fnRows = [
    ['#', '式 (y = ...)', '色', '表示'],
    ...data.functions.map((f: { expression: string; color: string; visible: boolean }, i: number) => [
      i + 1, f.expression, f.color, f.visible ? '表示' : '非表示',
    ]),
  ];
  const wsFn = XLSX.utils.aoa_to_sheet(fnRows);
  wsFn['!cols'] = [{ wch: 4 }, { wch: 30 }, { wch: 10 }, { wch: 8 }];
  XLSX.utils.book_append_sheet(wb, wsFn, '関数');

  const objRows = [
    ['#', '形状', 'X位置', 'Y位置', 'X速度', 'Y速度', '質量', 'サイズ', '色'],
    ...data.objects.map((o: ExcelExportData['objects'][0], i: number) => [
      i + 1, o.shape,
      Number(o.x.toFixed(4)), Number(o.y.toFixed(4)),
      Number(o.vx.toFixed(4)), Number(o.vy.toFixed(4)),
      o.mass, o.size, o.color,
    ]),
  ];
  const wsObj = XLSX.utils.aoa_to_sheet(objRows);
  wsObj['!cols'] = [{ wch: 4 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, wsObj, '物体');

  const settingsRows = [
    ['設定項目', '値'],
    ['X最小', data.settings.xMin],
    ['X最大', data.settings.xMax],
    ['Y最小', data.settings.yMin],
    ['Y最大', data.settings.yMax],
  ];
  const wsSettings = XLSX.utils.aoa_to_sheet(settingsRows);
  wsSettings['!cols'] = [{ wch: 14 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, wsSettings, '設定');

  if (data.trailData && data.trailData.length > 0) {
    data.trailData.forEach((td: { objectId: string; points: { x: number; y: number }[] }, idx: number) => {
      const trailRows = [
        [`物体 ${idx + 1} 軌跡 (ID: ${td.objectId})`],
        ['点番号', 'X', 'Y'],
        ...td.points.map((p: { x: number; y: number }, i: number) => [i + 1, Number(p.x.toFixed(6)), Number(p.y.toFixed(6))]),
      ];
      const wsTrail = XLSX.utils.aoa_to_sheet(trailRows);
      wsTrail['!cols'] = [{ wch: 8 }, { wch: 14 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, wsTrail, `軌跡_物体${idx + 1}`);
    });
  }

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

export interface VideoRecordOptions {
  opts: ExportRenderOptions;
  durationSec: number;
  fps: number;
  onProgress?: (progress: number) => void;
}

export async function renderToVideo(
  recordOpts: VideoRecordOptions,
  getFrameCanvas: (frameIndex: number, totalFrames: number) => HTMLCanvasElement
): Promise<{ blob: Blob; ext: string }> {
  const { durationSec, fps, onProgress } = recordOpts;
  const totalFrames = Math.round(durationSec * fps);

  const { width, height, scale } = recordOpts.opts;
  const W = Math.round(width * scale);
  const H = Math.round(height * scale);

  const offscreen = document.createElement('canvas');
  offscreen.width = W;
  offscreen.height = H;
  const ctx = offscreen.getContext('2d')!;

  const stream = offscreen.captureStream(fps);
  const mimeType = MediaRecorder.isTypeSupported('video/mp4;codecs=avc1')
    ? 'video/mp4;codecs=avc1'
    : MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
    ? 'video/webm;codecs=vp9'
    : 'video/webm';

  const ext = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';

  const chunks: Blob[] = [];
  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 8_000_000 });
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

  return new Promise((resolve, reject) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      resolve({ blob, ext });
    };
    recorder.onerror = (e) => reject(e);

    recorder.start();

    let frameIndex = 0;
    const msPerFrame = 1000 / fps;

    function renderNextFrame() {
      if (frameIndex >= totalFrames) {
        recorder.stop();
        return;
      }
      const frameCanvas = getFrameCanvas(frameIndex, totalFrames);
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(frameCanvas, 0, 0);
      onProgress?.(frameIndex / totalFrames);
      frameIndex++;
      setTimeout(renderNextFrame, msPerFrame);
    }

    renderNextFrame();
  });
}

export function renderToSVG(opts: ExportRenderOptions): string {
  const { functions, objects, settings, width, height,
    showGrid, showAxisLabels, showObjects, showObjectOrientation,
    visibleFunctionIds, visibleObjectIds, bgColor, theme, title } = opts;

  const { xMin, xMax, yMin, yMax } = settings;
  const coordSys = { settings, width, height };

  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const pxPerUnitX = width / xRange;
  const pxPerUnitY = height / yRange;
  const pxPerUnit = Math.min(pxPerUnitX, pxPerUnitY);
  const refPxPerUnit = width / 20;
  const zoomRatio = pxPerUnit / refPxPerUnit;
  const zoomLog = Math.log2(Math.max(zoomRatio, 0.01));

  const adaptiveStepX = calcAdaptiveStep(xMin, xMax, width, 70);
  const adaptiveStepY = calcAdaptiveStep(yMin, yMax, height, 55);
  const gridStep = Math.max(adaptiveStepX, adaptiveStepY);
  const minorStep = gridStep / 5;
  const fnLineWidth = Math.max(1.0, Math.min(4.0, 2.2 + zoomLog * 0.35));
  const fontSize = Math.max(9, Math.min(14, 11 + zoomLog * 1.2));

  const isDark = theme === 'dark';
  const gridMajorColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)';
  const gridMinorColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const axisColor      = isDark ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)';
  const labelColor     = isDark ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.55)';

  const bgFill = bgColor === 'transparent' ? 'none'
    : bgColor === 'theme' ? (isDark ? '#0f0f1e' : '#f5f7ff')
    : bgColor;

  const lines: string[] = [];
  lines.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`);
  lines.push(`<rect width="${width}" height="${height}" fill="${bgFill}"/>`);

  if (showGrid) {
    const minorPxGap = minorStep * pxPerUnitX;
    if (minorPxGap >= 10) {
      for (let gx = Math.ceil(xMin / minorStep) * minorStep; gx <= xMax + 1e-9; gx += minorStep) {
        const { cx } = worldToCanvas(gx, 0, coordSys);
        lines.push(`<line x1="${cx.toFixed(2)}" y1="0" x2="${cx.toFixed(2)}" y2="${height}" stroke="${gridMinorColor}" stroke-width="0.4"/>`);
      }
      for (let gy = Math.ceil(yMin / minorStep) * minorStep; gy <= yMax + 1e-9; gy += minorStep) {
        const { cy } = worldToCanvas(0, gy, coordSys);
        lines.push(`<line x1="0" y1="${cy.toFixed(2)}" x2="${width}" y2="${cy.toFixed(2)}" stroke="${gridMinorColor}" stroke-width="0.4"/>`);
      }
    }
    for (let gx = Math.ceil(xMin / gridStep) * gridStep; gx <= xMax + 1e-9; gx += gridStep) {
      const { cx } = worldToCanvas(gx, 0, coordSys);
      lines.push(`<line x1="${cx.toFixed(2)}" y1="0" x2="${cx.toFixed(2)}" y2="${height}" stroke="${gridMajorColor}" stroke-width="0.8"/>`);
    }
    for (let gy = Math.ceil(yMin / gridStep) * gridStep; gy <= yMax + 1e-9; gy += gridStep) {
      const { cy } = worldToCanvas(0, gy, coordSys);
      lines.push(`<line x1="0" y1="${cy.toFixed(2)}" x2="${width}" y2="${cy.toFixed(2)}" stroke="${gridMajorColor}" stroke-width="0.8"/>`);
    }
  }

  const origin = worldToCanvas(0, 0, coordSys);
  lines.push(`<line x1="0" y1="${origin.cy.toFixed(2)}" x2="${width}" y2="${origin.cy.toFixed(2)}" stroke="${axisColor}" stroke-width="1.8"/>`);
  lines.push(`<line x1="${origin.cx.toFixed(2)}" y1="0" x2="${origin.cx.toFixed(2)}" y2="${height}" stroke="${axisColor}" stroke-width="1.8"/>`);

  if (showAxisLabels) {
    for (let gx = Math.ceil(xMin / gridStep) * gridStep; gx <= xMax + 1e-9; gx += gridStep) {
      if (Math.abs(gx) < gridStep * 0.001) continue;
      const { cx } = worldToCanvas(gx, 0, coordSys);
      const labelY = Math.min(Math.max(origin.cy + 4 + fontSize, 4 + fontSize), height - 2);
      lines.push(`<text x="${cx.toFixed(2)}" y="${labelY.toFixed(2)}" text-anchor="middle" font-size="${fontSize}" fill="${labelColor}" font-family="Inter,sans-serif">${fmtTick(gx, gridStep)}</text>`);
    }
    for (let gy = Math.ceil(yMin / gridStep) * gridStep; gy <= yMax + 1e-9; gy += gridStep) {
      if (Math.abs(gy) < gridStep * 0.001) continue;
      const { cy } = worldToCanvas(0, gy, coordSys);
      const labelX = Math.min(Math.max(origin.cx - 6, 6), width - 6);
      lines.push(`<text x="${labelX.toFixed(2)}" y="${cy.toFixed(2)}" text-anchor="end" dominant-baseline="middle" font-size="${fontSize}" fill="${labelColor}" font-family="Inter,sans-serif">${fmtTick(gy, gridStep)}</text>`);
    }
  }

  functions
    .filter((f) => f.visible && !f.error && f.expression.trim() && visibleFunctionIds.has(f.id))
    .forEach((fn) => {
      const sampleCount = Math.round(Math.min(2000, Math.max(600, width)));
      const pts = computeGraphPoints(fn.expression, xMin, xMax, sampleCount);
      const pathParts: string[] = [];
      let penDown = false;
      pts.forEach(({ x: wx, y: wy }) => {
        if (isNaN(wy) || !isFinite(wy) || wy < yMin - 100 || wy > yMax + 100) {
          penDown = false; return;
        }
        const { cx, cy } = worldToCanvas(wx, wy, coordSys);
        if (!penDown) { pathParts.push(`M${cx.toFixed(2)},${cy.toFixed(2)}`); penDown = true; }
        else pathParts.push(`L${cx.toFixed(2)},${cy.toFixed(2)}`);
      });
      if (pathParts.length > 0) {
        lines.push(`<path d="${pathParts.join(' ')}" stroke="${fn.color}" stroke-width="${fnLineWidth.toFixed(2)}" fill="none" stroke-linejoin="round"/>`);
      }
    });

  if (showObjects) {
    objects
      .filter((o) => visibleObjectIds.has(o.id))
      .forEach((obj) => {
        const { cx, cy } = worldToCanvas(obj.x, obj.y, coordSys);
        // ワールド単位の半径をキャンバスピクセルに変換
        const radiusWorld = obj.size / 2;
        const r = radiusWorld * pxPerUnitY;
        if (obj.x < xMin - radiusWorld || obj.x > xMax + radiusWorld ||
            obj.y < yMin - radiusWorld || obj.y > yMax + radiusWorld) return;

        const angleDeg = (obj.angle * 180) / Math.PI;
        lines.push(`<g transform="translate(${cx.toFixed(2)},${cy.toFixed(2)}) rotate(${angleDeg.toFixed(2)})">`);

        if (obj.shape === 'circle') {
          lines.push(`<circle r="${r}" fill="${obj.color}" stroke="rgba(255,255,255,0.3)" stroke-width="1.5"/>`);
          if (showObjectOrientation) {
            lines.push(`<line x1="0" y1="0" x2="${r}" y2="0" stroke="rgba(255,255,255,0.7)" stroke-width="2"/>`);
          }
        } else if (obj.shape === 'square') {
          lines.push(`<rect x="${-r}" y="${-r}" width="${r * 2}" height="${r * 2}" fill="${obj.color}" stroke="rgba(255,255,255,0.3)" stroke-width="1.5"/>`);
          if (showObjectOrientation) {
            lines.push(`<line x1="${-r}" y1="0" x2="${r}" y2="0" stroke="rgba(255,255,255,0.25)" stroke-width="1"/>`);
            lines.push(`<line x1="0" y1="${-r}" x2="0" y2="${r}" stroke="rgba(255,255,255,0.25)" stroke-width="1"/>`);
          }
        } else if (obj.shape === 'triangle') {
          lines.push(`<polygon points="0,${-r} ${(r * 0.866).toFixed(2)},${(r * 0.5).toFixed(2)} ${(-r * 0.866).toFixed(2)},${(r * 0.5).toFixed(2)}" fill="${obj.color}" stroke="rgba(255,255,255,0.3)" stroke-width="1.5"/>`);
          if (showObjectOrientation) {
            lines.push(`<line x1="0" y1="0" x2="0" y2="${-r}" stroke="rgba(255,255,255,0.5)" stroke-width="2"/>`);
          }
        }
        lines.push(`</g>`);
      });
  }

  // Title in SVG
  if (title && (title.text.trim() || title.showDate)) {
    const dateStr = title.showDate
      ? new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
      : '';
    const textLines: string[] = [];
    if (title.text.trim()) textLines.push(title.text.trim());
    if (dateStr) textLines.push(dateStr);

    const weight = title.fontWeight === 'bold' ? '700' : title.fontWeight === 'light' ? '300' : '400';
    const lineHeight = title.fontSize * 1.4;
    const totalH = textLines.length * lineHeight;
    const padding = 16;

    let x: number, y: number, anchor: string;
    switch (title.position) {
      case 'top-left':     x = padding; y = padding + title.fontSize; anchor = 'start'; break;
      case 'top-center':   x = width / 2; y = padding + title.fontSize; anchor = 'middle'; break;
      case 'top-right':    x = width - padding; y = padding + title.fontSize; anchor = 'end'; break;
      case 'bottom-left':  x = padding; y = height - padding - totalH + title.fontSize; anchor = 'start'; break;
      case 'bottom-center':x = width / 2; y = height - padding - totalH + title.fontSize; anchor = 'middle'; break;
      case 'bottom-right': x = width - padding; y = height - padding - totalH + title.fontSize; anchor = 'end'; break;
    }

    // Background rect
    const bgAlpha = isDark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.55)';
    const maxW = textLines.reduce((m, l) => Math.max(m, l.length * title.fontSize * 0.6), 0);
    const bgW = maxW + 24;
    const bgH = totalH + 12;
    let bgX = x;
    if (title.position.includes('center')) bgX = x - bgW / 2;
    if (title.position.includes('right')) bgX = x - bgW;
    const bgY = y - title.fontSize - 6;
    lines.push(`<rect x="${bgX.toFixed(1)}" y="${bgY.toFixed(1)}" width="${bgW.toFixed(1)}" height="${bgH.toFixed(1)}" rx="8" fill="${bgAlpha}"/>`);

    textLines.forEach((line, i) => {
      lines.push(`<text x="${x.toFixed(1)}" y="${(y + i * lineHeight).toFixed(1)}" text-anchor="${anchor}" font-size="${title.fontSize}" fill="${title.color}" font-family="Inter,sans-serif" font-weight="${weight}">${line}</text>`);
    });
  }

  lines.push(`</svg>`);
  return lines.join('\n');
}
